import React, { useState, useEffect } from 'react';
import { BomberosHeader } from '../../components/BomberosHeader';
import { ModalValidacion } from '../../components/bomberos/ModalValidacion';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useBomberosPermisos } from '../../hooks/useBomberosPermisos';
import {
    bomberosCapacitacionAPI,
    bomberosPersonalAPI,
    bomberosAsistenciaAPI,
    bomberosEvaluacionAPI,
    bomberosCertificadoAPI,
} from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Checkbox } from '../../components/ui/checkbox';
import { Skeleton } from '../../components/ui/skeleton';
import { Progress } from '../../components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '../../components/ui/table';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../../components/ui/select';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '../../components/ui/dialog';
import { toast } from 'sonner';
import {
    ArrowLeft,
    GraduationCap,
    Calendar,
    Clock,
    MapPin,
    Users,
    Edit,
    CheckCircle,
    XCircle,
    UserPlus,
    ClipboardList,
    Award,
    Save,
    PlayCircle,
    BookOpen,
    FileDown,
    Download,
    Upload,
    FileImage,
    Trash2,
    FileCheck,
    Search,
} from 'lucide-react';

export default function CapacitacionDetallePage() {
    const navigate = useNavigate();
    const { id } = useParams();
    const { user } = useAuth();
    const { tienePermiso, esAdmin } = useBomberosPermisos();
    
    const [loading, setLoading] = useState(true);
    const [capacitacion, setCapacitacion] = useState(null);
    const [inscritos, setInscritos] = useState([]);
    const [asistencias, setAsistencias] = useState([]);
    const [evaluaciones, setEvaluaciones] = useState([]);
    const [bomberos, setBomberos] = useState([]);
    
    // Dialog states
    const [showInscribirDialog, setShowInscribirDialog] = useState(false);
    const [showAsistenciaDialog, setShowAsistenciaDialog] = useState(false);
    const [showEvaluacionDialog, setShowEvaluacionDialog] = useState(false);
    const [showValidacionDialog, setShowValidacionDialog] = useState(false);
    const [saving, setSaving] = useState(false);
    
    const puedeValidar = tienePermiso('validar_capacitaciones') || esAdmin;
    
    // Form states
    const [searchInscribir, setSearchInscribir] = useState('');
    const [selectedBomberos, setSelectedBomberos] = useState([]);
    const [asistenciaForm, setAsistenciaForm] = useState({
        fecha: new Date().toISOString().split('T')[0],
        asistentes: {},
    });
    const [documentosRespaldo, setDocumentosRespaldo] = useState([]);
    const [uploadingDoc, setUploadingDoc] = useState(false);
    const [evaluacionForm, setEvaluacionForm] = useState({
        bombero_id: '',
        resultado: 'aprobado',
        nota: '',
        observacion: '',
    });

    useEffect(() => {
        loadData();
    }, [id]);

    const loadData = async () => {
        try {
            setLoading(true);
            const [capRes, bomberosRes] = await Promise.all([
                bomberosCapacitacionAPI.getById(id),
                bomberosPersonalAPI.getAll({ activo: true }),
            ]);
            
            setCapacitacion(capRes.data);
            setBomberos(bomberosRes.data);
            setInscritos(capRes.data.inscritos || []);
            
            // Cargar asistencias y evaluaciones
            try {
                const [asistRes, evalRes] = await Promise.all([
                    bomberosAsistenciaAPI.getByCapacitacion(id),
                    bomberosEvaluacionAPI.getByCapacitacion(id),
                ]);
                setAsistencias(asistRes.data || []);
                setEvaluaciones(evalRes.data || []);
            } catch (err) {
                // Si no hay asistencias/evaluaciones, usar arrays vacíos
                setAsistencias([]);
                setEvaluaciones([]);
            }
        } catch (err) {
            console.error('Error loading data:', err);
            toast.error('Error al cargar datos');
            navigate('/bomberos/capacitaciones');
        } finally {
            setLoading(false);
        }
    };

    const getEstadoBadge = (estado) => {
        switch (estado) {
            case 'programada':
                return <Badge className="bg-blue-100 text-blue-700"><Calendar className="h-3 w-3 mr-1" />Programada</Badge>;
            case 'en_curso':
                return <Badge className="bg-green-100 text-green-700"><PlayCircle className="h-3 w-3 mr-1" />En Curso</Badge>;
            case 'finalizada':
                return <Badge className="bg-gray-100 text-gray-700"><CheckCircle className="h-3 w-3 mr-1" />Finalizada</Badge>;
            case 'cancelada':
                return <Badge className="bg-red-100 text-red-700"><XCircle className="h-3 w-3 mr-1" />Cancelada</Badge>;
            default:
                return <Badge variant="outline">{estado}</Badge>;
        }
    };

    const getValidacionBadge = (estado) => {
        switch (estado) {
            case 'autorizado':
                return <Badge className="bg-green-500 hover:bg-green-600 text-white"><CheckCircle className="h-3 w-3 mr-1" />Autorizado</Badge>;
            case 'rechazado':
                return <Badge variant="destructive"><XCircle className="h-3 w-3 mr-1" />Rechazado</Badge>;
            default:
                return <Badge variant="secondary"><FileCheck className="h-3 w-3 mr-1" />Pendiente Validación</Badge>;
        }
    };

    const handleInscribirBomberos = async () => {
        try {
            setSaving(true);
            const agregados = selectedBomberos.filter(id => !inscritos.includes(id));
            const quitados = inscritos.filter(id => !selectedBomberos.includes(id));
            
            if (agregados.length === 0 && quitados.length === 0) {
                toast.info('No hay cambios que guardar');
                setShowInscribirDialog(false);
                return;
            }

            await bomberosCapacitacionAPI.update(id, { inscritos: selectedBomberos });
            
            const msgs = [];
            if (agregados.length > 0) msgs.push(`${agregados.length} inscrito(s)`);
            if (quitados.length > 0) msgs.push(`${quitados.length} removido(s)`);
            toast.success(msgs.join(', '));
            
            setShowInscribirDialog(false);
            setSelectedBomberos([]);
            loadData();
        } catch (err) {
            toast.error('Error al actualizar inscritos');
        } finally {
            setSaving(false);
        }
    };

    const handleDesinscribir = async (bomberoId) => {
        const nombre = getBomberoNombre(bomberoId);
        if (!window.confirm(`¿Quitar a ${nombre} de esta capacitación?`)) return;
        try {
            setSaving(true);
            const nuevosInscritos = inscritos.filter(id => id !== bomberoId);
            await bomberosCapacitacionAPI.update(id, { inscritos: nuevosInscritos });
            toast.success(`${nombre} removido de la capacitación`);
            loadData();
        } catch (err) {
            toast.error('Error al quitar bombero');
        } finally {
            setSaving(false);
        }
    };

    const handleRegistrarAsistencia = async () => {
        if (!asistenciaForm.fecha) {
            toast.error('Seleccione una fecha');
            return;
        }

        try {
            setSaving(true);
            
            // Si hay documentos, subirlos primero
            let docUrls = [];
            if (documentosRespaldo.length > 0) {
                setUploadingDoc(true);
                try {
                    docUrls = await bomberosAsistenciaAPI.uploadDocumentos(
                        documentosRespaldo, 
                        id, 
                        asistenciaForm.fecha
                    );
                } catch (err) {
                    toast.error('Error al subir documentos de respaldo');
                } finally {
                    setUploadingDoc(false);
                }
            }
            
            // Registrar asistencia para cada bombero inscrito
            const registros = inscritos.map(bomberoId => ({
                capacitacion_id: id,
                bombero_id: bomberoId,
                fecha: asistenciaForm.fecha,
                presente: asistenciaForm.asistentes[bomberoId] || false,
                documento_respaldo_url: docUrls[0] || null,
                documentos_respaldo_urls: docUrls,
            }));

            for (const registro of registros) {
                await bomberosAsistenciaAPI.registrar(registro);
            }

            toast.success('Asistencia registrada correctamente');
            setShowAsistenciaDialog(false);
            setAsistenciaForm({ fecha: new Date().toISOString().split('T')[0], asistentes: {} });
            setDocumentosRespaldo([]);
            loadData();
        } catch (err) {
            toast.error('Error al registrar asistencia');
        } finally {
            setSaving(false);
        }
    };

    const handleRegistrarEvaluacion = async () => {
        if (!evaluacionForm.bombero_id) {
            toast.error('Seleccione un bombero');
            return;
        }

        try {
            setSaving(true);
            
            await bomberosEvaluacionAPI.registrar({
                capacitacion_id: id,
                bombero_id: evaluacionForm.bombero_id,
                fecha_evaluacion: new Date().toISOString().split('T')[0],
                resultado: evaluacionForm.resultado,
                nota: evaluacionForm.nota ? parseFloat(evaluacionForm.nota) : null,
                observacion: evaluacionForm.observacion,
                evaluador_id: user.id,
            });

            toast.success('Evaluación registrada');
            setShowEvaluacionDialog(false);
            setEvaluacionForm({ bombero_id: '', resultado: 'aprobado', nota: '', observacion: '' });
            loadData();
        } catch (err) {
            toast.error('Error al registrar evaluación');
        } finally {
            setSaving(false);
        }
    };

    const getBomberoNombre = (bomberoId) => {
        const bombero = bomberos.find(b => b.id === bomberoId);
        return bombero?.nombre_completo || 'Desconocido';
    };

    const toggleBomberoSelection = (bomberoId) => {
        setSelectedBomberos(prev => 
            prev.includes(bomberoId)
                ? prev.filter(id => id !== bomberoId)
                : [...prev, bomberoId]
        );
    };

    const toggleAsistencia = (bomberoId) => {
        setAsistenciaForm(prev => ({
            ...prev,
            asistentes: {
                ...prev.asistentes,
                [bomberoId]: !prev.asistentes[bomberoId],
            },
        }));
    };

    if (loading) {
        return (
            <div className="space-y-6 p-6">
                <Skeleton className="h-10 w-64" />
                <Skeleton className="h-48 w-full" />
                <Skeleton className="h-64 w-full" />
            </div>
        );
    }

    if (!capacitacion) {
        return null;
    }

    const porcentajeAsistencia = inscritos.length > 0
        ? Math.round((asistencias.filter(a => a.presente).length / (inscritos.length * asistencias.length / inscritos.length || 1)) * 100)
        : 0;

    const totalAprobados = evaluaciones.filter(e => e.resultado === 'aprobado').length;

    return (
        <>
            <BomberosHeader />
            <div className="space-y-4 md:space-y-6 p-4 md:p-6 pt-20 md:pt-20">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div className="flex items-center gap-3 md:gap-4">
                    <Button variant="ghost" size="icon" onClick={() => navigate('/bomberos/capacitaciones')} data-testid="back-btn">
                        <ArrowLeft className="h-5 w-5" />
                    </Button>
                    <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-2 mb-1">
                            {getEstadoBadge(capacitacion.estado)}
                            {getValidacionBadge(capacitacion.estado_validacion)}
                            <span className="text-xs sm:text-sm text-muted-foreground font-mono truncate">{capacitacion.codigo}</span>
                        </div>
                        <h1 className="text-lg sm:text-xl md:text-2xl font-bold flex items-center gap-2">
                            <GraduationCap className="h-5 w-5 md:h-6 md:w-6 text-purple-500 shrink-0" />
                            <span className="truncate">{capacitacion.nombre}</span>
                        </h1>
                    </div>
                </div>
                <div className="flex gap-2 w-full sm:w-auto">
                    {puedeValidar && capacitacion.estado_validacion !== 'autorizado' && capacitacion.estado_validacion !== 'rechazado' && (
                        <Button onClick={() => setShowValidacionDialog(true)} data-testid="validar-btn" className="flex-1 sm:flex-initial">
                            <FileCheck className="h-4 w-4 mr-2" />
                            Validar
                        </Button>
                    )}
                    <Button variant="outline" onClick={() => navigate(`/bomberos/capacitaciones/${id}/editar`)} data-testid="edit-btn" className="flex-1 sm:flex-initial">
                        <Edit className="h-4 w-4 mr-2" />
                        Editar
                    </Button>
                </div>
            </div>

            {/* Info Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
                <Card className="border">
                    <CardContent className="p-3 md:pt-4 md:pb-4 text-center">
                        <Calendar className="h-5 w-5 md:h-6 md:w-6 mx-auto mb-1 md:mb-2 text-blue-500" />
                        <p className="text-xs sm:text-sm font-medium truncate">{capacitacion.fecha_inicio}</p>
                        <p className="text-xs text-muted-foreground truncate">al {capacitacion.fecha_fin}</p>
                    </CardContent>
                </Card>
                <Card className="border">
                    <CardContent className="p-3 md:pt-4 md:pb-4 text-center">
                        <Clock className="h-5 w-5 md:h-6 md:w-6 mx-auto mb-1 md:mb-2 text-orange-500" />
                        <p className="text-lg md:text-xl font-bold">{capacitacion.horas_duracion}h</p>
                        <p className="text-xs text-muted-foreground">Duración</p>
                    </CardContent>
                </Card>
                <Card className="border">
                    <CardContent className="p-3 md:pt-4 md:pb-4 text-center">
                        <Users className="h-5 w-5 md:h-6 md:w-6 mx-auto mb-1 md:mb-2 text-green-500" />
                        <p className="text-lg md:text-xl font-bold">{inscritos.length}/{capacitacion.cupo_maximo || '-'}</p>
                        <p className="text-xs text-muted-foreground">Inscritos</p>
                    </CardContent>
                </Card>
                <Card className="border">
                    <CardContent className="p-3 md:pt-4 md:pb-4 text-center">
                        <Award className="h-5 w-5 md:h-6 md:w-6 mx-auto mb-1 md:mb-2 text-purple-500" />
                        <p className="text-lg md:text-xl font-bold">{totalAprobados}</p>
                        <p className="text-xs text-muted-foreground">Aprobados</p>
                    </CardContent>
                </Card>
            </div>

            {/* Main Content */}
            <Tabs defaultValue="info" className="space-y-4">
                <TabsList className="w-full flex-wrap h-auto gap-1">
                    <TabsTrigger value="info" data-testid="tab-info" className="text-xs sm:text-sm">
                        <BookOpen className="h-3 w-3 sm:h-4 sm:w-4 mr-1 sm:mr-2" />
                        <span className="hidden sm:inline">Información</span>
                        <span className="sm:hidden">Info</span>
                    </TabsTrigger>
                    <TabsTrigger value="inscritos" data-testid="tab-inscritos" className="text-xs sm:text-sm">
                        <Users className="h-3 w-3 sm:h-4 sm:w-4 mr-1 sm:mr-2" />
                        <span className="hidden sm:inline">Inscritos ({inscritos.length})</span>
                        <span className="sm:hidden">{inscritos.length}</span>
                    </TabsTrigger>
                    <TabsTrigger value="asistencia" data-testid="tab-asistencia" className="text-xs sm:text-sm">
                        <ClipboardList className="h-3 w-3 sm:h-4 sm:w-4 mr-1 sm:mr-2" />
                        <span className="hidden sm:inline">Asistencia</span>
                        <span className="sm:hidden">Asist.</span>
                    </TabsTrigger>
                    <TabsTrigger value="evaluaciones" data-testid="tab-evaluaciones" className="text-xs sm:text-sm">
                        <Award className="h-3 w-3 sm:h-4 sm:w-4 mr-1 sm:mr-2" />
                        <span className="hidden sm:inline">Evaluaciones</span>
                        <span className="sm:hidden">Eval.</span>
                    </TabsTrigger>
                </TabsList>

                {/* Tab: Información */}
                <TabsContent value="info" className="space-y-4">
                    <Card className="border-2">
                        <CardHeader>
                            <CardTitle>Detalles de la Capacitación</CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div className="space-y-3">
                                    <div>
                                        <p className="text-sm text-muted-foreground">Nivel ANB</p>
                                        <Badge className="capitalize mt-1">{capacitacion.nivel_anb}</Badge>
                                    </div>
                                    {capacitacion.compania_nombre && (
                                        <div>
                                            <p className="text-sm text-muted-foreground">Compañía Destinataria</p>
                                            <p className="font-medium">{capacitacion.compania_nombre}</p>
                                        </div>
                                    )}
                                    {!capacitacion.compania_id && (
                                        <div>
                                            <p className="text-sm text-muted-foreground">Compañía Destinataria</p>
                                            <Badge variant="outline">Todas las Compañías</Badge>
                                        </div>
                                    )}
                                    {capacitacion.competencia_nombre && (
                                        <div>
                                            <p className="text-sm text-muted-foreground">Competencia que Otorga</p>
                                            <p className="font-medium">{capacitacion.competencia_nombre}</p>
                                        </div>
                                    )}
                                    {capacitacion.instructor && (
                                        <div>
                                            <p className="text-sm text-muted-foreground">Instructor</p>
                                            <p className="font-medium flex items-center gap-2">
                                                <Users className="h-4 w-4" />
                                                {capacitacion.instructor}
                                            </p>
                                        </div>
                                    )}
                                    {capacitacion.lugar && (
                                        <div>
                                            <p className="text-sm text-muted-foreground">Lugar</p>
                                            <p className="font-medium flex items-center gap-2">
                                                <MapPin className="h-4 w-4" />
                                                {capacitacion.lugar}
                                            </p>
                                        </div>
                                    )}
                                </div>
                                <div className="space-y-3">
                                    {capacitacion.descripcion && (
                                        <div>
                                            <p className="text-sm text-muted-foreground">Descripción</p>
                                            <p>{capacitacion.descripcion}</p>
                                        </div>
                                    )}
                                    {capacitacion.objetivos && (
                                        <div>
                                            <p className="text-sm text-muted-foreground">Objetivos</p>
                                            <p className="whitespace-pre-line">{capacitacion.objetivos}</p>
                                        </div>
                                    )}
                                </div>
                            </div>
                            {capacitacion.contenidos && (
                                <div className="pt-4 border-t">
                                    <p className="text-sm text-muted-foreground mb-2">Contenidos / Temario</p>
                                    <p className="whitespace-pre-line">{capacitacion.contenidos}</p>
                                </div>
                            )}
                        </CardContent>
                    </Card>
                </TabsContent>

                {/* Tab: Inscritos */}
                <TabsContent value="inscritos" className="space-y-4">
                    <Card className="border-2">
                        <CardHeader className="pb-3">
                            <div className="flex items-center justify-between">
                                <CardTitle>Bomberos Inscritos</CardTitle>
                                <Button onClick={() => setShowInscribirDialog(true)} data-testid="inscribir-btn">
                                    <Users className="h-4 w-4 mr-2" />
                                    Gestionar Inscritos
                                </Button>
                            </div>
                        </CardHeader>
                        <CardContent>
                            {inscritos.length === 0 ? (
                                <div className="text-center py-8 text-muted-foreground">
                                    <Users className="h-12 w-12 mx-auto mb-4 opacity-50" />
                                    <p>No hay bomberos inscritos</p>
                                </div>
                            ) : (
                                <Table>
                                    <TableHeader>
                                        <TableRow>
                                            <TableHead>Bombero</TableHead>
                                            <TableHead>Asistencias</TableHead>
                                            <TableHead>Evaluación</TableHead>
                                            <TableHead className="w-12"></TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {inscritos.map(bomberoId => {
                                            const asistsBombero = asistencias.filter(a => a.bombero_id === bomberoId && a.presente);
                                            const evalBombero = evaluaciones.find(e => e.bombero_id === bomberoId);
                                            
                                            return (
                                                <TableRow key={bomberoId}>
                                                    <TableCell className="font-medium">{getBomberoNombre(bomberoId)}</TableCell>
                                                    <TableCell>
                                                        <Badge variant="outline">{asistsBombero.length} asistencias</Badge>
                                                    </TableCell>
                                                    <TableCell>
                                                        {evalBombero ? (
                                                            <Badge className={evalBombero.resultado === 'aprobado' ? 'bg-green-500' : 'bg-red-500'}>
                                                                {evalBombero.resultado === 'aprobado' ? 'Aprobado' : 'Reprobado'}
                                                                {evalBombero.nota && ` (${evalBombero.nota})`}
                                                            </Badge>
                                                        ) : (
                                                            <span className="text-muted-foreground text-sm">Sin evaluar</span>
                                                        )}
                                                    </TableCell>
                                                    <TableCell>
                                                        <Button
                                                            variant="ghost"
                                                            size="sm"
                                                            className="text-destructive hover:text-destructive h-8 w-8 p-0"
                                                            onClick={() => handleDesinscribir(bomberoId)}
                                                            disabled={saving}
                                                            title="Quitar de la capacitación"
                                                            data-testid={`desinscribir-${bomberoId}`}
                                                        >
                                                            <XCircle className="h-4 w-4" />
                                                        </Button>
                                                    </TableCell>
                                                </TableRow>
                                            );
                                        })}
                                    </TableBody>
                                </Table>
                            )}}
                        </CardContent>
                    </Card>
                </TabsContent>

                {/* Tab: Asistencia */}
                <TabsContent value="asistencia" className="space-y-4">
                    <Card className="border-2">
                        <CardHeader className="pb-3">
                            <div className="flex items-center justify-between">
                                <div>
                                    <CardTitle>Registro de Asistencia</CardTitle>
                                    <CardDescription>Control de asistencia por fecha</CardDescription>
                                </div>
                                <Button onClick={() => setShowAsistenciaDialog(true)} data-testid="registrar-asistencia-btn">
                                    <ClipboardList className="h-4 w-4 mr-2" />
                                    Registrar Asistencia
                                </Button>
                            </div>
                        </CardHeader>
                        <CardContent>
                            {asistencias.length === 0 ? (
                                <div className="text-center py-8 text-muted-foreground">
                                    <ClipboardList className="h-12 w-12 mx-auto mb-4 opacity-50" />
                                    <p>No hay registros de asistencia</p>
                                </div>
                            ) : (
                                <Table>
                                    <TableHeader>
                                        <TableRow>
                                            <TableHead>Fecha</TableHead>
                                            <TableHead>Bombero</TableHead>
                                            <TableHead>Estado</TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {asistencias.map((asist, idx) => (
                                            <TableRow key={idx}>
                                                <TableCell>{asist.fecha}</TableCell>
                                                <TableCell>{asist.bombero_nombre || getBomberoNombre(asist.bombero_id)}</TableCell>
                                                <TableCell>
                                                    {asist.presente ? (
                                                        <Badge className="bg-green-500"><CheckCircle className="h-3 w-3 mr-1" />Presente</Badge>
                                                    ) : (
                                                        <Badge className="bg-red-500"><XCircle className="h-3 w-3 mr-1" />Ausente</Badge>
                                                    )}
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            )}
                        </CardContent>
                    </Card>
                </TabsContent>

                {/* Tab: Evaluaciones */}
                <TabsContent value="evaluaciones" className="space-y-4">
                    <Card className="border-2">
                        <CardHeader className="pb-3">
                            <div className="flex items-center justify-between">
                                <div>
                                    <CardTitle>Evaluaciones</CardTitle>
                                    <CardDescription>Resultados de evaluaciones de participantes</CardDescription>
                                </div>
                                <Button onClick={() => setShowEvaluacionDialog(true)} data-testid="registrar-evaluacion-btn">
                                    <Award className="h-4 w-4 mr-2" />
                                    Registrar Evaluación
                                </Button>
                            </div>
                        </CardHeader>
                        <CardContent>
                            {evaluaciones.length === 0 ? (
                                <div className="text-center py-8 text-muted-foreground">
                                    <Award className="h-12 w-12 mx-auto mb-4 opacity-50" />
                                    <p>No hay evaluaciones registradas</p>
                                </div>
                            ) : (
                                <Table>
                                    <TableHeader>
                                        <TableRow>
                                            <TableHead>Bombero</TableHead>
                                            <TableHead>Fecha</TableHead>
                                            <TableHead>Resultado</TableHead>
                                            <TableHead>Nota</TableHead>
                                            <TableHead>Observación</TableHead>
                                            <TableHead className="w-32">Certificado</TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {evaluaciones.map((evalItem, idx) => (
                                            <TableRow key={idx}>
                                                <TableCell className="font-medium">{evalItem.bombero_nombre || getBomberoNombre(evalItem.bombero_id)}</TableCell>
                                                <TableCell>{evalItem.fecha_evaluacion}</TableCell>
                                                <TableCell>
                                                    <Badge className={evalItem.resultado === 'aprobado' ? 'bg-green-500' : evalItem.resultado === 'reprobado' ? 'bg-red-500' : 'bg-yellow-500'}>
                                                        {evalItem.resultado === 'aprobado' ? 'Aprobado' : evalItem.resultado === 'reprobado' ? 'Reprobado' : 'Pendiente'}
                                                    </Badge>
                                                </TableCell>
                                                <TableCell>{evalItem.nota || '-'}</TableCell>
                                                <TableCell className="max-w-xs truncate">{evalItem.observacion || '-'}</TableCell>
                                                <TableCell>
                                                    {evalItem.resultado === 'aprobado' && (
                                                        <Button
                                                            variant="outline"
                                                            size="sm"
                                                            onClick={() => {
                                                                const url = bomberosCertificadoAPI.descargar(id, evalItem.bombero_id);
                                                                const token = localStorage.getItem('token');
                                                                // Open in new tab with token
                                                                window.open(`${url}?token=${token}`, '_blank');
                                                            }}
                                                            data-testid={`cert-${evalItem.bombero_id}`}
                                                        >
                                                            <Download className="h-4 w-4 mr-1" />
                                                            PDF
                                                        </Button>
                                                    )}
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            )}
                        </CardContent>
                    </Card>
                </TabsContent>
            </Tabs>

            {/* Dialog: Gestionar Inscritos */}
            <Dialog open={showInscribirDialog} onOpenChange={(open) => { 
                setShowInscribirDialog(open); 
                if (!open) { setSearchInscribir(''); setSelectedBomberos([]); }
                if (open) { setSelectedBomberos([...inscritos]); setSearchInscribir(''); }
            }}>
                <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle>Gestionar Inscritos</DialogTitle>
                        <DialogDescription>
                            Marque o desmarque bomberos para inscribir o quitar de la capacitación
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-3 py-4">
                        {/* Buscador predictivo */}
                        <div className="relative">
                            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                            <Input
                                placeholder="Buscar por nombre, RUT o compañía..."
                                value={searchInscribir}
                                onChange={(e) => setSearchInscribir(e.target.value)}
                                className="pl-10"
                                data-testid="search-inscribir-bombero"
                                autoFocus
                            />
                        </div>
                        {(() => {
                            const agregados = selectedBomberos.filter(sid => !inscritos.includes(sid)).length;
                            const quitados = inscritos.filter(sid => !selectedBomberos.includes(sid)).length;
                            if (agregados > 0 || quitados > 0) {
                                return (
                                    <div className="flex gap-2 text-xs">
                                        {agregados > 0 && <Badge className="bg-green-100 text-green-700">+{agregados} nuevo(s)</Badge>}
                                        {quitados > 0 && <Badge className="bg-red-100 text-red-700">-{quitados} a quitar</Badge>}
                                    </div>
                                );
                            }
                            return null;
                        })()}
                        <div className="max-h-[45vh] overflow-y-auto space-y-1">
                            {bomberos
                                .filter(b => {
                                    if (capacitacion.compania_id && b.compania_id !== capacitacion.compania_id) return false;
                                    if (searchInscribir) {
                                        const term = searchInscribir.toLowerCase();
                                        return (
                                            b.nombre_completo?.toLowerCase().includes(term) ||
                                            b.rut?.toLowerCase().includes(term) ||
                                            b.compania_nombre?.toLowerCase().includes(term)
                                        );
                                    }
                                    return true;
                                })
                                .sort((a, b) => {
                                    const aInscrito = selectedBomberos.includes(a.id) ? 0 : 1;
                                    const bInscrito = selectedBomberos.includes(b.id) ? 0 : 1;
                                    return aInscrito - bInscrito;
                                })
                                .map(bombero => {
                                    const estaInscrito = inscritos.includes(bombero.id);
                                    const estaSeleccionado = selectedBomberos.includes(bombero.id);
                                    return (
                                        <div key={bombero.id} className={`flex items-center space-x-2 p-2 rounded transition-colors ${estaInscrito && !estaSeleccionado ? 'bg-red-50 hover:bg-red-100' : estaSeleccionado && !estaInscrito ? 'bg-green-50 hover:bg-green-100' : 'hover:bg-muted'}`}>
                                            <Checkbox
                                                id={`bombero-${bombero.id}`}
                                                checked={estaSeleccionado}
                                                onCheckedChange={() => toggleBomberoSelection(bombero.id)}
                                            />
                                            <label htmlFor={`bombero-${bombero.id}`} className="flex-1 cursor-pointer">
                                                <p className="font-medium text-sm">
                                                    {bombero.nombre_completo}
                                                    {estaInscrito && estaSeleccionado && <span className="text-xs text-muted-foreground ml-2">(inscrito)</span>}
                                                </p>
                                                <p className="text-xs text-muted-foreground">{bombero.rut} - {bombero.compania_nombre} - {bombero.grado}</p>
                                            </label>
                                        </div>
                                    );
                                })}
                            {bomberos.filter(b => {
                                if (capacitacion.compania_id && b.compania_id !== capacitacion.compania_id) return false;
                                if (searchInscribir) {
                                    const term = searchInscribir.toLowerCase();
                                    return (
                                        b.nombre_completo?.toLowerCase().includes(term) ||
                                        b.rut?.toLowerCase().includes(term) ||
                                        b.compania_nombre?.toLowerCase().includes(term)
                                    );
                                }
                                return true;
                            }).length === 0 && (
                                <p className="text-center text-muted-foreground py-4">
                                    {searchInscribir
                                        ? 'No se encontraron bomberos con ese criterio'
                                        : 'No hay bomberos disponibles'
                                    }
                                </p>
                            )}
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowInscribirDialog(false)}>Cancelar</Button>
                        <Button 
                            onClick={handleInscribirBomberos} 
                            disabled={saving || (selectedBomberos.filter(sid => !inscritos.includes(sid)).length === 0 && inscritos.filter(sid => !selectedBomberos.includes(sid)).length === 0)}
                            data-testid="guardar-inscritos-btn"
                        >
                            <Save className="h-4 w-4 mr-2" />
                            {saving ? 'Guardando...' : 'Guardar Cambios'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Dialog: Registrar Asistencia */}
            <Dialog open={showAsistenciaDialog} onOpenChange={setShowAsistenciaDialog}>
                <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle>Registrar Asistencia</DialogTitle>
                        <DialogDescription>Marque los bomberos presentes</DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                        <div className="space-y-2">
                            <label className="text-sm font-medium">Fecha</label>
                            <Input
                                type="date"
                                value={asistenciaForm.fecha}
                                onChange={(e) => setAsistenciaForm(prev => ({ ...prev, fecha: e.target.value }))}
                            />
                        </div>
                        
                        {/* Campo de carga de documento */}
                        <div className="space-y-2">
                            <label className="text-sm font-medium">Documento de Respaldo (opcional)</label>
                            <div className="flex items-center gap-2">
                                <Input
                                    type="file"
                                    accept="image/*,.pdf"
                                    multiple
                                    onChange={(e) => setDocumentosRespaldo(prev => [...prev, ...Array.from(e.target.files)])}
                                    className="flex-1"
                                />
                                {documentosRespaldo.length > 0 && (
                                    <Button 
                                        variant="ghost" 
                                        size="sm"
                                        onClick={() => setDocumentosRespaldo([])}
                                        className="text-destructive"
                                    >
                                        <Trash2 className="h-4 w-4" />
                                    </Button>
                                )}
                            </div>
                            {documentosRespaldo.length > 0 && (
                                <div className="space-y-1">
                                    {documentosRespaldo.map((doc, idx) => (
                                        <div key={idx} className="flex items-center gap-2 text-sm text-muted-foreground">
                                            <FileImage className="h-4 w-4 shrink-0" />
                                            <span className="truncate flex-1">{doc.name}</span>
                                            <Button variant="ghost" size="sm" className="h-6 w-6 p-0 text-destructive"
                                                onClick={() => setDocumentosRespaldo(prev => prev.filter((_, i) => i !== idx))}>
                                                <Trash2 className="h-3 w-3" />
                                            </Button>
                                        </div>
                                    ))}
                                </div>
                            )}
                            <p className="text-xs text-muted-foreground">Imágenes o PDF de planillas de asistencia firmadas (puede seleccionar varios)</p>
                        </div>
                        
                        <div className="space-y-2">
                            <label className="text-sm font-medium">Bomberos Inscritos</label>
                            <div className="max-h-48 overflow-y-auto border rounded-lg p-2">
                                {inscritos.map(bomberoId => (
                                    <div key={bomberoId} className="flex items-center space-x-2 p-2 hover:bg-muted rounded">
                                        <Checkbox
                                            id={`asist-${bomberoId}`}
                                            checked={asistenciaForm.asistentes[bomberoId] || false}
                                            onCheckedChange={() => toggleAsistencia(bomberoId)}
                                        />
                                        <label htmlFor={`asist-${bomberoId}`} className="flex-1 cursor-pointer text-sm">
                                            {getBomberoNombre(bomberoId)}
                                        </label>
                                    </div>
                                ))}
                                {inscritos.length === 0 && (
                                    <p className="text-center text-muted-foreground py-4">No hay bomberos inscritos</p>
                                )}
                            </div>
                        </div>
                    </div>
                    <DialogFooter className="flex-col sm:flex-row gap-2">
                        <Button variant="outline" onClick={() => setShowAsistenciaDialog(false)} className="w-full sm:w-auto">
                            Cancelar
                        </Button>
                        <Button onClick={handleRegistrarAsistencia} disabled={saving || uploadingDoc || inscritos.length === 0} className="w-full sm:w-auto">
                            {uploadingDoc ? (
                                <>
                                    <Upload className="h-4 w-4 mr-2 animate-pulse" />
                                    Subiendo documento...
                                </>
                            ) : (
                                <>
                                    <Save className="h-4 w-4 mr-2" />
                                    {saving ? 'Guardando...' : 'Guardar Asistencia'}
                                </>
                            )}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Dialog: Registrar Evaluación */}
            <Dialog open={showEvaluacionDialog} onOpenChange={setShowEvaluacionDialog}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Registrar Evaluación</DialogTitle>
                        <DialogDescription>Registre el resultado de evaluación de un participante</DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                        <div className="space-y-2">
                            <label className="text-sm font-medium">Bombero</label>
                            <Select
                                value={evaluacionForm.bombero_id}
                                onValueChange={(v) => setEvaluacionForm(prev => ({ ...prev, bombero_id: v }))}
                            >
                                <SelectTrigger>
                                    <SelectValue placeholder="Seleccionar bombero" />
                                </SelectTrigger>
                                <SelectContent>
                                    {inscritos
                                        .filter(id => !evaluaciones.find(e => e.bombero_id === id))
                                        .map(bomberoId => (
                                            <SelectItem key={bomberoId} value={bomberoId}>
                                                {getBomberoNombre(bomberoId)}
                                            </SelectItem>
                                        ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="space-y-2">
                            <label className="text-sm font-medium">Resultado</label>
                            <Select
                                value={evaluacionForm.resultado}
                                onValueChange={(v) => setEvaluacionForm(prev => ({ ...prev, resultado: v }))}
                            >
                                <SelectTrigger>
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="aprobado">Aprobado</SelectItem>
                                    <SelectItem value="reprobado">Reprobado</SelectItem>
                                    <SelectItem value="pendiente">Pendiente</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="space-y-2">
                            <label className="text-sm font-medium">Nota (opcional)</label>
                            <Input
                                type="number"
                                min="1"
                                max="7"
                                step="0.1"
                                value={evaluacionForm.nota}
                                onChange={(e) => setEvaluacionForm(prev => ({ ...prev, nota: e.target.value }))}
                                placeholder="Ej: 6.5"
                            />
                        </div>
                        <div className="space-y-2">
                            <label className="text-sm font-medium">Observación</label>
                            <Input
                                value={evaluacionForm.observacion}
                                onChange={(e) => setEvaluacionForm(prev => ({ ...prev, observacion: e.target.value }))}
                                placeholder="Observaciones de la evaluación"
                            />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowEvaluacionDialog(false)}>Cancelar</Button>
                        <Button onClick={handleRegistrarEvaluacion} disabled={saving || !evaluacionForm.bombero_id}>
                            <Award className="h-4 w-4 mr-2" />
                            {saving ? 'Guardando...' : 'Registrar'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Modal de Validación */}
            <ModalValidacion
                open={showValidacionDialog}
                onOpenChange={setShowValidacionDialog}
                item={capacitacion}
                itemType="capacitacion"
                onValidar={(data) => bomberosCapacitacionAPI.validar(id, data)}
                onSuccess={loadData}
            />
        </div>
        </>
    );
}
